import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { clearCurrentlyPlaying } from '../redux/slices/contentSlice';
import { updateContinueWatching } from '../redux/slices/userSlice';

const PlayerContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: black;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: black;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const VideoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, #1a1a1a, #2a2a2a);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  text-align: center;
`;

const PlaceholderTitle = styled.h2`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PlaceholderText = styled.p`
  font-size: 1.2rem;
  color: #808080;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Controls = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 2rem;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.3s ease;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ControlsTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const ControlsBottom = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const PlayButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.3rem;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ProgressContainer = styled.div`
  flex: 1;
  margin: 0 1rem;
  position: relative;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  cursor: pointer;

  @media (max-width: 768px) {
    margin: 0 0.5rem;
    height: 4px;
  }
`;

const ProgressBar = styled.div`
  height: 100%;
  background: #E50914;
  border-radius: 3px;
  width: ${props => props.$progress}%;
  transition: width 0.1s ease;
`;

const TimeDisplay = styled.div`
  color: white;
  font-size: 0.9rem;
  min-width: 120px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    min-width: 80px;
  }
`;

const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const VolumeSlider = styled.input`
  width: 80px;
  cursor: pointer;
`;

const FullscreenButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const VideoPlayer = () => {
  const dispatch = useDispatch();
  const { currentlyPlaying } = useSelector(state => state.content);
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [controlsTimeout, setControlsTimeout] = useState(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video && currentlyPlaying) {
      video.play().catch(console.error);
    }
  }, [currentlyPlaying]);

  useEffect(() => {
    const hideControls = () => {
      setShowControls(false);
    };

    if (controlsTimeout) {
      clearTimeout(controlsTimeout);
    }

    if (showControls) {
      const timeout = setTimeout(hideControls, 3000);
      setControlsTimeout(timeout);
    }

    return () => {
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
    };
  }, [showControls]);

  const handleBack = () => {
    if (currentlyPlaying) {
      const progressPercentage = (currentTime / duration) * 100;
      dispatch(updateContinueWatching({
        contentId: currentlyPlaying.id,
        progress: progressPercentage
      }));
    }
    dispatch(clearCurrentlyPlaying());
  };

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = (e) => {
    setCurrentTime(e.target.currentTime);
  };

  const handleLoadedMetadata = (e) => {
    setDuration(e.target.duration);
  };

  const handleProgressClick = (e) => {
    const video = videoRef.current;
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;

    if (video) {
      video.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  if (!currentlyPlaying) {
    return null;
  }

  return (
    <PlayerContainer onMouseMove={handleMouseMove}>
      <VideoWrapper>
        <VideoPlaceholder>
          <PlaceholderTitle>{currentlyPlaying.title}</PlaceholderTitle>
          <PlaceholderText>Video player simulation</PlaceholderText>
          <PlaceholderText>
            This is a mockup player. In a real implementation,
            <br />
            this would show the actual video content.
          </PlaceholderText>
        </VideoPlaceholder>

        <Controls $visible={showControls}>
          <ControlsTop>
            <BackButton onClick={handleBack} aria-label="Go back">
              ←
            </BackButton>
            <Title>{currentlyPlaying.title}</Title>
            <div />
          </ControlsTop>

          <ControlsBottom>
            <PlayButton
              onClick={handlePlayPause}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? '⏸️' : '▶️'}
            </PlayButton>

            <ControlButton aria-label="Rewind 10 seconds">
              ⏪
            </ControlButton>

            <ControlButton aria-label="Forward 10 seconds">
              ⏩
            </ControlButton>

            <TimeDisplay>
              {formatTime(currentTime)} / {formatTime(duration || 7200)}
            </TimeDisplay>

            <ProgressContainer onClick={handleProgressClick}>
              <ProgressBar $progress={(currentTime / (duration || 7200)) * 100} />
            </ProgressContainer>

            <VolumeContainer>
              <ControlButton aria-label="Mute">
                🔊
              </ControlButton>
              <VolumeSlider
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                aria-label="Volume"
              />
            </VolumeContainer>

            <ControlButton aria-label="Subtitles">
              CC
            </ControlButton>

            <ControlButton aria-label="Audio & Subtitles">
              🎵
            </ControlButton>

            <FullscreenButton onClick={toggleFullscreen} aria-label="Fullscreen">
              ⛶
            </FullscreenButton>
          </ControlsBottom>
        </Controls>
      </VideoWrapper>
    </PlayerContainer>
  );
};

export default VideoPlayer;